import { useState, type FormEvent } from 'react';
import { C, POLICE_CORPS, DEGRADE_ACTION } from '../../theme';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';
import { Champ } from '../composants/Champ';
import { Alerte, CadreAuth, LienTexte } from '../composants/CadreAuth';

/**
 * Fuseau propose par defaut : celui du telephone. Le fuseau appartient au FOYER, pas au
 * code — le premier testeur est a Dakar et se deplace. On le devine pour eviter une
 * question technique, et on laisse le corriger.
 */
function fuseauDetecte(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris';
  } catch {
    return 'Europe/Paris';
  }
}

const FUSEAUX_COURANTS = [
  { valeur: 'Europe/Paris', libelle: 'France (Paris)' },
  { valeur: 'Africa/Dakar', libelle: 'Sénégal (Dakar)' },
  { valeur: 'Europe/Brussels', libelle: 'Belgique (Bruxelles)' },
  { valeur: 'Europe/Zurich', libelle: 'Suisse (Zurich)' },
  { valeur: 'America/Montreal', libelle: 'Québec (Montréal)' },
  { valeur: 'Africa/Abidjan', libelle: "Côte d'Ivoire (Abidjan)" },
  { valeur: 'Africa/Casablanca', libelle: 'Maroc (Casablanca)' },
];

export function CreerFoyer({
  nomUtilisateur,
  onCree,
  onDeconnexion,
}: {
  nomUtilisateur: string;
  onCree: () => void;
  onDeconnexion: () => void;
}) {
  const detecte = fuseauDetecte();
  const [nom, setNom] = useState(`Foyer ${nomUtilisateur.trim().split(/\s+/).slice(-1)[0] ?? ''}`.trim());
  const [fuseau, setFuseau] = useState(detecte);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  // Le fuseau du telephone est propose meme s'il n'est pas dans la liste courte :
  // quelqu'un en voyage ne doit pas se voir imposer un fuseau faux.
  const choix = FUSEAUX_COURANTS.some((f) => f.valeur === detecte)
    ? FUSEAUX_COURANTS
    : [{ valeur: detecte, libelle: `Détecté sur ton téléphone (${detecte})` }, ...FUSEAUX_COURANTS];

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (envoi) return;
    if (nom.trim() === '') {
      setErreur('Donne un nom à ton foyer.');
      return;
    }
    setEnvoi(true);
    setErreur(null);
    try {
      await api.creerFoyer(nom.trim(), fuseau);
      onCree();
    } catch (cause) {
      setErreur(messageDErreur(cause));
      setEnvoi(false);
    }
  };

  return (
    <CadreAuth
      titre="Ton foyer"
      sousTitre="Colette organise la vie d'un foyer. Donne-lui un nom : tu pourras y ajouter les autres ensuite."
      pied={<LienTexte onClick={onDeconnexion}>Se déconnecter</LienTexte>}
    >
      <form onSubmit={soumettre} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Champ
          libelle="Nom du foyer"
          valeur={nom}
          onChange={setNom}
          aide="« Foyer Diop », « La maison », « Chez nous »… comme tu veux."
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor="fuseau" style={{ fontSize: 13, fontWeight: 700, color: C.secondaire }}>
            Où vivez-vous ?
          </label>
          <select
            id="fuseau"
            value={fuseau}
            onChange={(e) => setFuseau(e.target.value)}
            style={{
              height: 48,
              border: `1px solid ${C.bordure}`,
              borderRadius: 12,
              padding: '0 12px',
              fontSize: 16,
              fontFamily: POLICE_CORPS,
              color: C.encre,
              background: C.carte,
              outline: 'none',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {choix.map((f) => (
              <option key={f.valeur} value={f.valeur}>
                {f.libelle}
              </option>
            ))}
          </select>
          <div style={{ fontSize: 12.5, color: C.tertiaire, lineHeight: 1.45 }}>
            Sert à placer tes rendez-vous à la bonne heure. Modifiable plus tard.
          </div>
        </div>

        {erreur !== null ? <Alerte message={erreur} /> : null}

        <button
          type="submit"
          disabled={envoi}
          style={{
            height: 52,
            border: 'none',
            borderRadius: 14,
            background: envoi ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            fontSize: 16,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: envoi ? 'default' : 'pointer',
            marginTop: 4,
          }}
        >
          {envoi ? 'Création…' : 'Créer mon foyer'}
        </button>
      </form>
    </CadreAuth>
  );
}
